import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { JOB_POSTING_PLANS } from '../../config/jobPostingPlans';
import { SALARY_CURRENCIES, SALARY_STRUCTURES } from '../../config/salary';
import { JOB_DEPARTMENTS } from '../../config/jobDepartments';
import { uploadJobJd } from './postJobApi';
import useAuthStore from '../../stores/authStore';

const JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Remote'];
const MAX_JOB_SKILLS = 10;
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');

function resolveMediaUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

function toIsoDate(value) {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf('day').toISOString() : null;
}

function JobDetailsStep({ initialData, onBack, onNext, onSaveDraft, saving, token }) {
  const authToken = token || useAuthStore.getState().token;
  const jdInputRef = useRef(null);
  const [uploadingJd, setUploadingJd] = useState(false);
  const [form, setForm] = useState({
    title: initialData.title || '',
    department: initialData.department || 'Engineering',
    location: initialData.location || 'Remote',
    type: initialData.type || 'Full-time',
    salaryFrom: initialData.salaryFrom ?? '',
    salaryTo: initialData.salaryTo ?? '',
    salaryCurrency: initialData.salaryCurrency || 'USD',
    salaryStructure: initialData.salaryStructure || 'annual',
    description: initialData.description || '',
    jdFileUrl: initialData.jdFileUrl || '',
    jdFileName: initialData.jdFileName || '',
    skills: Array.isArray(initialData.skills) ? initialData.skills.slice(0, MAX_JOB_SKILLS) : [],
    skillDraft: '',
    applicationEmail: initialData.applicationEmail || '',
    plan: initialData.plan || 'basic',
    startDate: initialData.startDate || null,
    deadlineDate: initialData.deadlineDate || null,
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleJdUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingJd(true);
    setError('');
    try {
      const data = await uploadJobJd(authToken, file);
      setForm((prev) => ({
        ...prev,
        jdFileUrl: data.jdFileUrl || '',
        jdFileName: data.jdFileName || file.name,
      }));
    } catch (err) {
      setError(err.message || 'Failed to upload JD file');
    } finally {
      setUploadingJd(false);
      if (jdInputRef.current) jdInputRef.current.value = '';
    }
  };

  const clearJdFile = () => {
    setForm((prev) => ({ ...prev, jdFileUrl: '', jdFileName: '' }));
  };

  const addSkill = () => {
    const next = String(form.skillDraft || '').trim();
    if (!next) return;
    if ((form.skills || []).length >= MAX_JOB_SKILLS) {
      setError(`You can add up to ${MAX_JOB_SKILLS} skills.`);
      return;
    }
    if ((form.skills || []).some((s) => s.toLowerCase() === next.toLowerCase())) {
      setForm((prev) => ({ ...prev, skillDraft: '' }));
      return;
    }
    setError('');
    setForm((prev) => ({
      ...prev,
      skills: [...(prev.skills || []), next],
      skillDraft: '',
    }));
  };

  const buildPayload = () => {
    const { skillDraft, ...rest } = form;
    return {
      ...rest,
      salaryFrom: form.salaryFrom === '' ? null : Number(form.salaryFrom),
      salaryTo: form.salaryTo === '' ? null : Number(form.salaryTo),
      salaryCurrency: form.salaryCurrency || 'USD',
      salaryStructure: form.salaryStructure || 'annual',
      startDate: toIsoDate(form.startDate),
      deadlineDate: toIsoDate(form.deadlineDate),
      skills: Array.isArray(form.skills)
        ? form.skills.map((s) => String(s || '').trim()).filter(Boolean).slice(0, MAX_JOB_SKILLS)
        : [],
      responsibilities: [],
      requirements: [],
      benefits: [],
    };
  };

  const validate = ({ requireDates }) => {
    if (!form.title.trim()) {
      setError('Job title is required.');
      return false;
    }

    const from = form.salaryFrom === '' ? null : Number(form.salaryFrom);
    const to = form.salaryTo === '' ? null : Number(form.salaryTo);
    if (form.salaryFrom !== '' && !Number.isFinite(from)) {
      setError('Salary from must be a valid number.');
      return false;
    }
    if (form.salaryTo !== '' && !Number.isFinite(to)) {
      setError('Salary to must be a valid number.');
      return false;
    }
    if (from !== null && to !== null && to < from) {
      setError('Salary "to" must be greater than or equal to salary "from".');
      return false;
    }

    if (requireDates) {
      if (!form.startDate) {
        setError('Job post start date is required.');
        return false;
      }
      if (!form.deadlineDate) {
        setError('Job post deadline date is required.');
        return false;
      }
    }

    if (form.startDate && form.deadlineDate) {
      const start = dayjs(form.startDate).startOf('day');
      const deadline = dayjs(form.deadlineDate).startOf('day');
      if (deadline.isBefore(start)) {
        setError('Deadline must be on or after the start date.');
        return false;
      }
    }

    setError('');
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate({ requireDates: true })) return;
    onNext(buildPayload());
  };

  const handleSaveDraft = () => {
    if (!validate({ requireDates: false })) return;
    onSaveDraft?.(buildPayload());
  };

  return (
    <Box className="post-job-card ch-card-enter">
      <Box className="post-job-card-header" sx={{ mb: 1.5 }}>
        <Typography className="post-job-eyebrow" variant="overline" color="primary">
          Step 2 of 3
        </Typography>
        <Typography variant="h5" component="h2" sx={{ mt: 0.25, mb: 0.5 }}>
          Job details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Describe the role, optionally upload a JD file, set posting dates, and choose a plan.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={1.25}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField
              label="Job title"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Senior Frontend Engineer"
              required
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="job-department-label">Department</InputLabel>
              <Select
                labelId="job-department-label"
                name="department"
                label="Department"
                value={
                  JOB_DEPARTMENTS.includes(form.department)
                    ? form.department
                    : form.department || 'Engineering'
                }
                onChange={handleChange}
              >
                {!JOB_DEPARTMENTS.includes(form.department) && form.department ? (
                  <MenuItem value={form.department}>{form.department}</MenuItem>
                ) : null}
                {JOB_DEPARTMENTS.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField label="Location" name="location" value={form.location} onChange={handleChange} />
            <FormControl size="small" fullWidth>
              <InputLabel id="job-type-label">Employment type</InputLabel>
              <Select
                labelId="job-type-label"
                name="type"
                label="Employment type"
                value={form.type}
                onChange={handleChange}
              >
                {JOB_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <TextField
              label="Salary from"
              name="salaryFrom"
              type="number"
              value={form.salaryFrom}
              onChange={handleChange}
              placeholder="80000"
              inputProps={{ min: 0, step: 1 }}
            />
            <TextField
              label="Salary to"
              name="salaryTo"
              type="number"
              value={form.salaryTo}
              onChange={handleChange}
              placeholder="120000"
              inputProps={{ min: 0, step: 1 }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="salary-currency-label">Currency</InputLabel>
              <Select
                labelId="salary-currency-label"
                name="salaryCurrency"
                label="Currency"
                value={form.salaryCurrency}
                onChange={handleChange}
              >
                {SALARY_CURRENCIES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="salary-structure-label">Structure</InputLabel>
              <Select
                labelId="salary-structure-label"
                name="salaryStructure"
                label="Structure"
                value={form.salaryStructure}
                onChange={handleChange}
              >
                {SALARY_STRUCTURES.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <DatePicker
              label="Job post start date"
              value={form.startDate ? dayjs(form.startDate) : null}
              onChange={(value) => setForm({ ...form, startDate: value })}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  required: true,
                },
              }}
            />
            <DatePicker
              label="Deadline date"
              value={form.deadlineDate ? dayjs(form.deadlineDate) : null}
              onChange={(value) => setForm({ ...form, deadlineDate: value })}
              minDate={form.startDate ? dayjs(form.startDate) : undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  required: true,
                  helperText: 'Listing is removed automatically after this date.',
                },
              }}
            />
          </Stack>

          <TextField
            label="Job description"
            name="description"
            value={form.description}
            onChange={handleChange}
            multiline
            minRows={12}
            maxRows={24}
          />

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              JD file (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Upload a PDF, DOC, or DOCX job description (max 10MB).
            </Typography>
            <input
              ref={jdInputRef}
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              hidden
              onChange={handleJdUpload}
            />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button
                type="button"
                variant="outlined"
                size="small"
                disabled={uploadingJd || saving}
                onClick={() => jdInputRef.current?.click()}
              >
                {uploadingJd ? 'Uploading...' : form.jdFileUrl ? 'Replace JD file' : 'Upload JD file'}
              </Button>
              {form.jdFileUrl ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {form.jdFileName || 'JD file uploaded'}
                  </Typography>
                  <Button
                    type="button"
                    size="small"
                    href={resolveMediaUrl(form.jdFileUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Preview
                  </Button>
                  <Button type="button" size="small" color="inherit" onClick={clearJdFile}>
                    Remove
                  </Button>
                </>
              ) : null}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Skills (max {MAX_JOB_SKILLS})
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
              <TextField
                label="Add a skill"
                placeholder="e.g. React, Python"
                name="skillDraft"
                value={form.skillDraft}
                onChange={handleChange}
                disabled={(form.skills || []).length >= MAX_JOB_SKILLS}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSkill();
                  }
                }}
                fullWidth
                size="small"
                helperText={`${(form.skills || []).length}/${MAX_JOB_SKILLS} skills`}
              />
              <Button
                type="button"
                variant="outlined"
                onClick={addSkill}
                disabled={(form.skills || []).length >= MAX_JOB_SKILLS}
                sx={{ flexShrink: 0 }}
              >
                Add skill
              </Button>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {(form.skills || []).map((skill) => (
                <Box
                  key={skill}
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: 'rgba(7, 132, 90, 0.1)',
                    color: '#056b48',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {skill}
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Remove ${skill}`}
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        skills: (prev.skills || []).filter((s) => s !== skill),
                      }))
                    }
                    sx={{
                      border: 'none',
                      background: 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      lineHeight: 1,
                      p: 0,
                    }}
                  >
                    ×
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          <TextField
            label="Applications email"
            name="applicationEmail"
            type="email"
            value={form.applicationEmail}
            onChange={handleChange}
            placeholder="hiring@yourcompany.com"
          />

          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
            Posting plan
          </Typography>

          <RadioGroup name="plan" value={form.plan} onChange={handleChange}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems="stretch"
              sx={{ width: '100%' }}
            >
              {JOB_POSTING_PLANS.map((plan) => (
                <Box
                  key={plan.id}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    border: '1.5px solid',
                    borderColor: form.plan === plan.id ? 'primary.main' : 'divider',
                    bgcolor: form.plan === plan.id ? 'rgba(7, 132, 90, 0.06)' : 'background.paper',
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.75,
                    transition: 'border-color 160ms ease, background-color 160ms ease',
                  }}
                >
                  <FormControlLabel
                    value={plan.id}
                    control={<Radio size="small" color="primary" />}
                    label={
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                          <Typography variant="subtitle2" color="text.primary">
                            {plan.name}
                          </Typography>
                          <Typography variant="body2" color="primary" fontWeight={700}>
                            {plan.price <= 0 ? 'Free' : `$${plan.price}`}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {plan.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
                  />
                </Box>
              ))}
            </Stack>
          </RadioGroup>

          <Box className="post-job-actions" sx={{ mt: 0.5 }}>
            <Button type="button" variant="outlined" onClick={onBack} disabled={saving || uploadingJd}>
              Back
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              onClick={handleSaveDraft}
              disabled={saving || uploadingJd}
            >
              {saving ? 'Saving...' : 'Save draft'}
            </Button>
            <Button type="submit" variant="contained" disabled={saving || uploadingJd}>
              {saving
                ? 'Saving...'
                : form.plan === 'basic'
                  ? 'Continue to publish'
                  : 'Continue to payment'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

export default JobDetailsStep;
